import {
    useLayoutEffect,
    useState
} from "react";
import {
    TouchableOpacity,
    Image,
    View,
    Platform
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitToast,
    NCoreUIKitTheme,
    PageContainer,
    TextAreaInput,
    TextInput,
    Button,
    Dialog,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute,
    type RouteProp
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    GetMyProfilePayloadType
} from "../../../../../services/restAPI/actions/getMyProfile/types";
import type {
    SendProfileUpdateRequestRequestType
} from "../../../../../services/restAPI/actions/sendProfileUpdateRequest/types";
import type AppStackParamList from "../../../../../types/navigation/app";
import type {
    SelectedFileType
} from "../../type";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ArrowLeftRight,
    AlertCircle,
    ChevronLeft,
    PencilOff,
    Pencil,
    Check
} from "lucide-react-native";
import ImageCropPicker from "react-native-image-crop-picker";
import validator from "validator";
import {
    RESTService
} from "../../../../../services/restAPI";
import PositionCard from "../../components/positionCard";
import {
    handleUpload
} from "../../../../../utils";

const EditProfileUpdateRequest = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

    const route = useRoute<RouteProp<AppStackParamList, "EditProfileUpdateRequest">>();

    const {
        user,
        updateRequest
    } = route.params;

    const {
        spaces,
        borders,
        radiuses,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const inset = useSafeAreaInsets();

    const [data,
        setData] = useState<GetMyProfilePayloadType>(user);
    const [updateParams,
        setUpdateParams] = useState<SendProfileUpdateRequestRequestType | undefined>({
            aboutMyTrade: updateRequest.aboutMyTrade ? updateRequest.aboutMyTrade: "",
            phoneNumber: updateRequest.phoneNumber ? updateRequest.phoneNumber : "",
            firstName: updateRequest.firstName ? updateRequest.firstName : "",
            lastName: updateRequest.lastName ? updateRequest.lastName : "",
            mail: updateRequest.mail ? updateRequest.mail : "",
            companies: updateRequest.companies ? updateRequest.companies : []
        });
    const [successDialog,
        setSuccessDialog] = useState(false);
    const [verificationDialog,
        setVerificationDialog] = useState(false);
    const [deleteSuccessDialog,
        setDeleteSuccessDialog] = useState(false);
    const [deleteVerificationDialog,
        setDeleteVerificationDialog] = useState(false);
    const [selectedImage,
        setSelectedImage] = useState<string | null>(null);
    const [selctedFile,
        setSelectedFile] = useState<SelectedFileType | null>(null);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={[
                    stylesheet.headerContainer,
                    {
                        paddingTop: inset.top,
                        paddingHorizontal: spaces.spacingMd,
                        backgroundColor: colors.content.container.default
                    }
                ]}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
                <View
                    style={[
                        stylesheet.headerTitleContainer,
                        {
                            top: inset.top + spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                        style={stylesheet.headerTitle}
                    >
                        {localize("editProfile")}
                    </Text>
                </View>
            </View>
        });
    }, [navigation]);

    const removeUpdateRequest = () => {
        RESTService.action("CancelProfileUpdateRequest", {
            requestID: updateRequest._id
        })
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setDeleteSuccessDialog(true);
                }

            }).catch((err) => {
                console.log(err);
            });
    };

    const sendProfileUpdateReq = () => {
        if(!updateParams) {
            if(selctedFile) {
                handleUpdateProfilePhoto();
                return;
            }
            NCoreUIKitToast.open({
                title: localize("emptyRequestError"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        const params: SendProfileUpdateRequestRequestType = {};

        if(updateParams.aboutMyTrade && updateParams.aboutMyTrade.length) {
            params.aboutMyTrade = updateParams.aboutMyTrade;
        }

        if(updateParams.firstName && updateParams.firstName.length) {
            params.firstName = updateParams.firstName;
        }

        if(updateParams.lastName && updateParams.lastName.length) {
            params.lastName = updateParams.lastName;
        }

        if(updateParams.phoneNumber && updateParams.phoneNumber.length && validator.isMobilePhone(updateParams.phoneNumber)) {
            params.phoneNumber = updateParams.phoneNumber;
        }

        if(updateParams.mail && updateParams.mail.length && validator.isEmail(updateParams.mail)) {
            params.mail = updateParams.mail;
        }

        if(updateParams.companies && updateParams.companies.length) {
            params.companies = updateParams.companies;
        }

        if (Object.keys(params).length === 0) {
            if(selctedFile) {
                handleUpdateProfilePhoto();
                return;
            }
            NCoreUIKitToast.open({
                title: localize("emptyRequestError"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        };
        RESTService.action("SendProfileUpdateRequest", params)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    if(selctedFile) {
                        handleUpdateProfilePhoto();
                        return;
                    }
                    setSuccessDialog(true);
                }
            }).catch((err) => {
                console.log(err);
            });
    };

    const handleUpdateProfilePhoto = () => {
        if(!selctedFile) {
            return;
        }

        RESTService.action("UploadProfilePhoto", null)
            .then(async (res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    await handleUpload(selctedFile, res.payload.uploadToken)
                        .then(() => {
                            setSuccessDialog(true);
                        })
                        .catch((err) => {
                            console.log("err", err);
                            throw err;
                        });
                }
            }).catch((err) => {
                console.log("err", err);
            });
    };

    const renderProfilePhoto = () => {
        return <View
            style={[
                stylesheet.profilePhotoWrapper,
                {
                    borderRadius: radiuses.full,
                    marginTop: spaces.spacingLg,
                    marginBottom: spaces.spacingXl
                }
            ]}
        >
            <Image
                source={{
                    uri: selectedImage ? selectedImage : user.profilePhotoURL
                }}
                resizeMode="cover"
                style={[
                    stylesheet.profilePhotoImage,
                    {
                        borderRadius: radiuses.full
                    }
                ]}
            />
            <TouchableOpacity
                style={[
                    stylesheet.photoEditButton,
                    {
                        right: -spaces.spacingSm,
                        bottom: -spaces.spacingSm,
                        backgroundColor: colors.content.container.mid,
                        borderRadius: radiuses.full,
                        padding: spaces.spacingSm,
                        borderWidth: borders.subtract,
                        borderColor: colors.content.border.subtle
                    }
                ]}
                onPress={() => {
                    ImageCropPicker.openPicker({
                        cropping: true,
                        multiple: false
                    }).then(image => {
                        const format = image.mime?.split("/")[1];

                        if (image.size > 20 * 1024 * 1024) {
                            NCoreUIKitToast.open({
                                title: localize("imageTooLarge"),
                                type: "warning"
                            });
                            return;
                        }

                        if (![
                            "jpg",
                            "jpeg",
                            "png",
                            "heic"
                        ].includes(format as string)) {
                            NCoreUIKitToast.open({
                                title: localize("invalidFormat"),
                                type: "warning"
                            });
                            return;
                        }

                        const selectedFile = {
                            name: image.path.split("/").pop(),
                            type: image.mime,
                            uri: Platform.OS === "ios" ? image.path.startsWith("file://") ? image.path : `file:/${image.path}` : image.path
                        } as unknown as { name: string; type: string; uri: string; };
                        setSelectedImage(image.path);
                        setSelectedFile(selectedFile);
                    }).catch((err) => {
                        console.error(err);
                    });
                }}
            >
                <Pencil
                    size={20}
                />
            </TouchableOpacity>
        </View>;
    };

    const isFormValid = Boolean(
        data.firstName && data.firstName.trim().length > 0 &&
        data.lastName && data.lastName.trim().length > 0 &&
        data.mail && validator.isEmail(data.mail) &&
        data.phoneNumber && validator.isMobilePhone(data.phoneNumber)
    );

    const renderGeneralInfo = () => {
        return <View>
            <Text
                variant="titleLargeSize"
                color="high"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                {localize("generalInformation")}
            </Text>
            <TextInput
                title={updateRequest.firstName ? `${localize("firstName")} ${localize("underReview")}` : localize("firstName")}
                type={!data.firstName || data.firstName.trim() === "" ? "danger" : "default"}
                hintText={!data.firstName || data.firstName.trim() === "" ? (localize("required-field")) : undefined}
                spreadBehaviour="stretch"
                placeholder={data.firstName}
                defaultValue={data.firstName}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        firstName: e
                    });
                    setUpdateParams({
                        ...(updateParams || {}),
                        firstName: e
                    } as unknown as SendProfileUpdateRequestRequestType);
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextInput
                title={updateRequest.lastName ? `${localize("lastName")} ${localize("underReview")}` : localize("lastName")}
                type={!data.lastName || data.lastName.trim() === "" ? "danger" : "default"}
                hintText={!data.lastName || data.lastName.trim() === "" ? (localize("required-field")) : undefined}
                spreadBehaviour="stretch"
                placeholder={data.lastName}
                defaultValue={data.lastName}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        lastName: e
                    });
                    setUpdateParams({
                        ...updateParams,
                        lastName: e
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextInput
                title={updateRequest.mail ? `${localize("emailAddress")} ${localize("underReview")}` : localize("emailAddress")}
                type={!data.mail || !validator.isEmail(data.mail) ? "danger" : "default"}
                hintText={!data.mail || !validator.isEmail(data.mail) ? (localize("invalid-email")) : undefined}
                spreadBehaviour="stretch"
                placeholder={data.mail}
                defaultValue={data.mail}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        mail: e
                    });
                    setUpdateParams({
                        ...updateParams,
                        mail: e
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextInput
                title={updateRequest.phoneNumber ? `${localize("phoneNumber")} ${localize("underReview")}` : localize("phoneNumber")}
                type={!data.phoneNumber || !validator.isMobilePhone(data.phoneNumber) ? "danger" : "default"}
                hintText={!data.phoneNumber || !validator.isMobilePhone(data.phoneNumber) ? (localize("invalid-phone")) : undefined}
                spreadBehaviour="stretch"
                placeholder={data.phoneNumber}
                defaultValue={data.phoneNumber}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        phoneNumber: e
                    });
                    setUpdateParams({
                        ...updateParams,
                        phoneNumber: e
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextAreaInput
                title={updateRequest.aboutMyTrade ? `${localize("about")} ${localize("underReview")}` : localize("about")}
                spreadBehaviour="stretch"
                placeholder={data.aboutMyTrade}
                defaultValue={data.aboutMyTrade}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        aboutMyTrade: e
                    });
                    setUpdateParams({
                        ...updateParams,
                        aboutMyTrade: e
                    });
                }}
                maxHeight={500}
                type="default"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
        </View>;
    };

    const renderPositions = () => {
        if(data.companies && data.companies.length) {
            return <View
                style={{
                    marginBottom: spaces.spacingXl
                }}
            >
                <Text
                    variant="titleMediumSize"
                    color="high"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                >
                    {localize("positions")}
                </Text>
                {
                    data.companies.map((e, index)=> {
                        let isEdit = false;
                        if(updateRequest && updateRequest.companies?.length) {
                            const _updatePosition =  updateRequest.companies.find(x => x.companyID === e.companyID);

                            if(_updatePosition) {
                                isEdit = true;
                            }
                        }

                        return <PositionCard
                            key={e.companyID}
                            companyLogo={e.companyLogo}
                            companyTitle={e.companyTitle}
                            setUpdateParams={setUpdateParams}
                            updateParams={updateParams}
                            companyID={e.companyID}
                            isEdit={isEdit}
                            disabled={false}
                            initialPosition={{
                                _id: e.positionID,
                                value: e.position
                            }}
                            objectIndex={index}
                        />;
                    })
                }
            </View>;
        } else {
            return null;
        }
    };

    const renderActionButtons = () => {
        return <View
            style={[
                stylesheet.actionButtonsContainer,
                {
                    paddingHorizontal: spaces.spacingXl,
                    marginBottom: spaces.spacingLg
                }
            ]}
        >
            <Button
                size="large"
                title={localize("updateEditRequest")}
                customColor="black"
                customBorderColor="black"
                spreadBehaviour="stretch"
                isDisabled={ !isFormValid || !updateParams }
                onPress={() => {
                    if(!isFormValid) {
                        let errorMsg = "Lütfen tüm zorunlu alanları geçerli bir şekilde doldurunuz.";
                        if (!data.firstName || data.firstName.trim() === "") errorMsg = localize("please-enter-first-name");
                        else if (!data.lastName || data.lastName.trim() === "") errorMsg = localize("please-enter-last-name");
                        else if (!data.mail || !validator.isEmail(data.mail)) errorMsg = localize("please-enter-valid-email");
                        else if (!data.phoneNumber || !validator.isMobilePhone(data.phoneNumber)) errorMsg = localize("please-enter-valid-phone");

                        NCoreUIKitToast.open({
                            title: localize("error"),
                            subTitle: errorMsg,
                            type: "danger"
                        });
                        return;
                    }
                    setVerificationDialog(true);
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <Button
                title={localize("cancelEditRequest")}
                type="danger"
                size="large"
                spreadBehaviour="stretch"
                onPress={() => {
                    setDeleteVerificationDialog(true);
                }}
            />
        </View>;
    };

    const renderSuccessDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            backgroundColor: colors.content.container.success,
                            padding: spaces.spacingMd,
                            borderRadius: radiuses.full,
                            marginBottom: spaces.spacingLg
                        }
                    ]}
                >
                    <Check
                        size={30}
                        color={colors.content.icon.success}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("submittedSuccessfully")}
                </Text>
            </View>}
            content={localize("changesUnderReview")}
            isVisible={successDialog}
            onOverlayPress={() => {
                setSuccessDialog(false);
                navigation.goBack();
            }}
            primaryButtonProps={{
                onPress: () => {
                    setSuccessDialog(false);
                    navigation.navigate({
                        name: "MainTabs",
                        params: {
                            screen: "HomeTab"
                        }
                    } as never);
                },
                type: "success",
                title: localize("backToHome")
            }}
            variant="ok"
        />;
    };

    const renderVerificationDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            marginBottom: spaces.spacingLg,
                            padding: spaces.spacingLg,
                            backgroundColor: colors.content.container.success,
                            borderRadius: radiuses.full
                        }
                    ]}
                >
                    <ArrowLeftRight
                        size={40}
                        color={colors.content.icon.success}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                    style={stylesheet.dialogTitle}
                >
                    {localize("confirmUpdateEditRequest")}
                </Text>
            </View>}
            content={localize("updateEditRequestDescription")}
            isVisible={verificationDialog}
            onOverlayPress={() => {
                setVerificationDialog(false);
            }}
            primaryButtonProps={{
                onPress: () => {
                    setVerificationDialog(false);
                    sendProfileUpdateReq();
                },
                customColor: "black",
                title: localize("update")
            }}
            secondaryButtonProps={{
                title: localize("abandon"),
                onPress: () => {
                    setVerificationDialog(false);
                }
            }}
            variant="yes-no"
        />;
    };

    const renderDeleteSuccessDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            backgroundColor: colors.content.container.warning,
                            padding: spaces.spacingMd,
                            borderRadius: radiuses.full,
                            marginBottom: spaces.spacingLg
                        }
                    ]}
                >
                    <Check
                        size={30}
                        color={colors.content.icon.warning}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("requestCancelled")}
                </Text>
            </View>}
            content={localize("editRequestWithdrawn")}
            isVisible={deleteSuccessDialog}
            onOverlayPress={() => {
                setDeleteSuccessDialog(false);
                navigation.goBack();
            }}
            primaryButtonProps={{
                onPress: () => {
                    setDeleteSuccessDialog(false);
                    navigation.goBack();
                },
                type: "warning",
                title: localize("goBack")
            }}
            variant="ok"
        />;
    };

    const renderDeleteVerificationDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            marginBottom: spaces.spacingLg,
                            padding: spaces.spacingLg,
                            backgroundColor: colors.content.container.warning,
                            borderRadius: radiuses.full
                        }
                    ]}
                >
                    <PencilOff
                        size={40}
                        color={colors.content.icon.warning}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                    style={stylesheet.dialogTitle}
                >
                    {localize("confirmCancelEditRequest")}
                </Text>
            </View>}
            content={localize("cancelEditRequestDescription")}
            isVisible={deleteVerificationDialog}
            onOverlayPress={() => {
                setDeleteVerificationDialog(false);
            }}
            primaryButtonProps={{
                onPress: () => {
                    setDeleteVerificationDialog(false);
                    removeUpdateRequest();
                },
                type: "danger",
                title: localize("cancel-it")
            }}
            secondaryButtonProps={{
                title: localize("abandon"),
                onPress: () => {
                    setDeleteVerificationDialog(false);
                }
            }}
            variant="yes-no"
        />;
    };

    return <PageContainer
        backgroundColor="default"
        isScrollable={true}
        scrollViewProps={{
            showsVerticalScrollIndicator: false
        }}
    >
        {renderProfilePhoto()}
        {renderGeneralInfo()}
        {renderPositions()}
        {renderActionButtons()}
        {renderSuccessDialog()}
        {renderVerificationDialog()}
        {renderDeleteVerificationDialog()}
        {renderDeleteSuccessDialog()}
    </PageContainer>;
};
export default EditProfileUpdateRequest;
